import { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartConfig,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { UsageChartData } from "@/types/billing";
import { TrendingUp, TrendingDown, Activity } from "lucide-react";

interface AgencyUsageChartProps {
  data: UsageChartData[];
  isLoading?: boolean;
  title?: string;
  description?: string;
}

const chartConfig: ChartConfig = {
  voice: {
    label: "Voice",
    color: "hsl(var(--chart-1))",
  },
  sms: {
    label: "SMS",
    color: "hsl(var(--chart-2))",
  },
  transcription: {
    label: "Transcription",
    color: "hsl(var(--chart-3))",
  },
  ai: {
    label: "AI Analysis",
    color: "hsl(var(--chart-4))",
  },
};

const formatCurrency = (value: number): string => {
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}k`;
  }
  return `$${value.toFixed(0)}`;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export function AgencyUsageChart({
  data,
  isLoading,
  title = "Usage Overview",
  description = "Total usage costs across all clients",
}: AgencyUsageChartProps) {
  // Calculate trend
  const trend = useMemo(() => {
    if (data.length < 2) return null;

    const recent = data.slice(-7);
    const previous = data.slice(-14, -7);

    if (previous.length === 0) return null;

    const recentTotal = recent.reduce((sum, d) => sum + d.total, 0);
    const previousTotal = previous.reduce((sum, d) => sum + d.total, 0);

    if (previousTotal === 0) return null;

    const percentChange = ((recentTotal - previousTotal) / previousTotal) * 100;
    return {
      direction: percentChange >= 0 ? "up" : "down",
      value: Math.abs(percentChange).toFixed(1),
    };
  }, [data]);

  // Calculate totals by service
  const serviceTotals = useMemo(() => {
    return data.reduce(
      (acc, d) => ({
        voice: acc.voice + d.voice,
        sms: acc.sms + d.sms,
        transcription: acc.transcription + d.transcription,
        ai: acc.ai + d.ai,
        total: acc.total + d.total,
      }),
      { voice: 0, sms: 0, transcription: 0, ai: 0, total: 0 }
    );
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-60 mt-1" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-[300px]">
          <div className="text-center text-muted-foreground">
            <Activity className="h-12 w-12 mx-auto mb-2 opacity-50" />
            <p>No usage data available</p>
            <p className="text-sm">Usage will appear once clients start using services.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          {trend && (
            <div
              className={`flex items-center gap-1 text-sm ${
                trend.direction === "up" ? "text-green-600" : "text-red-600"
              }`}
            >
              {trend.direction === "up" ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              <span>{trend.value}%</span>
              <span className="text-muted-foreground text-xs">vs last week</span>
            </div>
          )}
        </div>

        {/* Service breakdown mini stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <div className="text-center p-2 rounded-lg bg-[hsl(var(--chart-1))]/10">
            <p className="text-xs text-muted-foreground">Voice</p>
            <p className="font-medium text-[hsl(var(--chart-1))]">
              {formatCurrency(serviceTotals.voice)}
            </p>
          </div>
          <div className="text-center p-2 rounded-lg bg-[hsl(var(--chart-2))]/10">
            <p className="text-xs text-muted-foreground">SMS</p>
            <p className="font-medium text-[hsl(var(--chart-2))]">
              {formatCurrency(serviceTotals.sms)}
            </p>
          </div>
          <div className="text-center p-2 rounded-lg bg-[hsl(var(--chart-3))]/10">
            <p className="text-xs text-muted-foreground">Transcription</p>
            <p className="font-medium text-[hsl(var(--chart-3))]">
              {formatCurrency(serviceTotals.transcription)}
            </p>
          </div>
          <div className="text-center p-2 rounded-lg bg-[hsl(var(--chart-4))]/10">
            <p className="text-xs text-muted-foreground">AI</p>
            <p className="font-medium text-[hsl(var(--chart-4))]">
              {formatCurrency(serviceTotals.ai)}
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorVoice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-voice)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-voice)" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorSms" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-sms)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-sms)" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorTranscription" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-transcription)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-transcription)" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorAi" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-ai)" stopOpacity={0.8} />
                <stop offset="95%" stopColor="var(--color-ai)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={formatCurrency}
              tick={{ fontSize: 12 }}
              tickLine={false}
              axisLine={false}
              width={50}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  labelFormatter={(label) => formatDate(label as string)}
                  formatter={(value, name) => [
                    formatCurrency(value as number),
                    chartConfig[name as keyof typeof chartConfig]?.label || name,
                  ]}
                />
              }
            />
            <Area
              type="monotone"
              dataKey="voice"
              stackId="1"
              stroke="var(--color-voice)"
              fill="url(#colorVoice)"
            />
            <Area
              type="monotone"
              dataKey="sms"
              stackId="1"
              stroke="var(--color-sms)"
              fill="url(#colorSms)"
            />
            <Area
              type="monotone"
              dataKey="transcription"
              stackId="1"
              stroke="var(--color-transcription)"
              fill="url(#colorTranscription)"
            />
            <Area
              type="monotone"
              dataKey="ai"
              stackId="1"
              stroke="var(--color-ai)"
              fill="url(#colorAi)"
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
